"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, Search } from "lucide-react";
import { formatPhoneNumber } from "@/lib/validators/phone";

/**
 * Country data with dial code and flag emoji
 * Sorted with Germany first, then alphabetically by name
 */
const COUNTRIES = [
// Germany first (default)
{
  code: "DE",
  name: "Deutschland",
  dial: "+49",
  flag: "🇩🇪"
},
// Then alphabetically
{
  code: "AT",
  name: "Österreich",
  dial: "+43",
  flag: "🇦🇹"
}, {
  code: "CH",
  name: "Schweiz",
  dial: "+41",
  flag: "🇨🇭"
}, {
  code: "LI",
  name: "Liechtenstein",
  dial: "+423",
  flag: "🇱🇮"
}, {
  code: "LU",
  name: "Luxemburg",
  dial: "+352",
  flag: "🇱🇺"
}, {
  code: "BE",
  name: "Belgien",
  dial: "+32",
  flag: "🇧🇪"
}, {
  code: "NL",
  name: "Niederlande",
  dial: "+31",
  flag: "🇳🇱"
}, {
  code: "FR",
  name: "Frankreich",
  dial: "+33",
  flag: "🇫🇷"
}, {
  code: "PL",
  name: "Polen",
  dial: "+48",
  flag: "🇵🇱"
}, {
  code: "CZ",
  name: "Tschechien",
  dial: "+420",
  flag: "🇨🇿"
}, {
  code: "DK",
  name: "Dänemark",
  dial: "+45",
  flag: "🇩🇰"
},
// Divider - rest of Europe
{
  code: "AL",
  name: "Albanien",
  dial: "+355",
  flag: "🇦🇱"
}, {
  code: "AD",
  name: "Andorra",
  dial: "+376",
  flag: "🇦🇩"
}, {
  code: "BY",
  name: "Belarus",
  dial: "+375",
  flag: "🇧🇾"
}, {
  code: "BA",
  name: "Bosnien und Herzegowina",
  dial: "+387",
  flag: "🇧🇦"
}, {
  code: "BG",
  name: "Bulgarien",
  dial: "+359",
  flag: "🇧🇬"
}, {
  code: "HR",
  name: "Kroatien",
  dial: "+385",
  flag: "🇭🇷"
}, {
  code: "CY",
  name: "Zypern",
  dial: "+357",
  flag: "🇨🇾"
}, {
  code: "EE",
  name: "Estland",
  dial: "+372",
  flag: "🇪🇪"
}, {
  code: "FI",
  name: "Finnland",
  dial: "+358",
  flag: "🇫🇮"
}, {
  code: "GR",
  name: "Griechenland",
  dial: "+30",
  flag: "🇬🇷"
}, {
  code: "HU",
  name: "Ungarn",
  dial: "+36",
  flag: "🇭🇺"
}, {
  code: "IS",
  name: "Island",
  dial: "+354",
  flag: "🇮🇸"
}, {
  code: "IE",
  name: "Irland",
  dial: "+353",
  flag: "🇮🇪"
}, {
  code: "IT",
  name: "Italien",
  dial: "+39",
  flag: "🇮🇹"
}, {
  code: "XK",
  name: "Kosovo",
  dial: "+383",
  flag: "🇽🇰"
}, {
  code: "LV",
  name: "Lettland",
  dial: "+371",
  flag: "🇱🇻"
}, {
  code: "LT",
  name: "Litauen",
  dial: "+370",
  flag: "🇱🇹"
}, {
  code: "MK",
  name: "Nordmazedonien",
  dial: "+389",
  flag: "🇲🇰"
}, {
  code: "MT",
  name: "Malta",
  dial: "+356",
  flag: "🇲🇹"
}, {
  code: "MD",
  name: "Moldau",
  dial: "+373",
  flag: "🇲🇩"
}, {
  code: "MC",
  name: "Monaco",
  dial: "+377",
  flag: "🇲🇨"
}, {
  code: "ME",
  name: "Montenegro",
  dial: "+382",
  flag: "🇲🇪"
}, {
  code: "NO",
  name: "Norwegen",
  dial: "+47",
  flag: "🇳🇴"
}, {
  code: "PT",
  name: "Portugal",
  dial: "+351",
  flag: "🇵🇹"
}, {
  code: "RO",
  name: "Rumänien",
  dial: "+40",
  flag: "🇷🇴"
}, {
  code: "RU",
  name: "Russland",
  dial: "+7",
  flag: "🇷🇺"
}, {
  code: "SM",
  name: "San Marino",
  dial: "+378",
  flag: "🇸🇲"
}, {
  code: "RS",
  name: "Serbien",
  dial: "+381",
  flag: "🇷🇸"
}, {
  code: "SK",
  name: "Slowakei",
  dial: "+421",
  flag: "🇸🇰"
}, {
  code: "SI",
  name: "Slowenien",
  dial: "+386",
  flag: "🇸🇮"
}, {
  code: "ES",
  name: "Spanien",
  dial: "+34",
  flag: "🇪🇸"
}, {
  code: "SE",
  name: "Schweden",
  dial: "+46",
  flag: "🇸🇪"
}, {
  code: "UA",
  name: "Ukraine",
  dial: "+380",
  flag: "🇺🇦"
}, {
  code: "GB",
  name: "Vereinigtes Königreich",
  dial: "+44",
  flag: "🇬🇧"
}, {
  code: "VA",
  name: "Vatikanstadt",
  dial: "+39",
  flag: "🇻🇦"
},
// Non-European countries (commonly used)
{
  code: "US",
  name: "USA",
  dial: "+1",
  flag: "🇺🇸"
}, {
  code: "CA",
  name: "Kanada",
  dial: "+1",
  flag: "🇨🇦"
}, {
  code: "AU",
  name: "Australien",
  dial: "+61",
  flag: "🇦🇺"
}, {
  code: "NZ",
  name: "Neuseeland",
  dial: "+64",
  flag: "🇳🇿"
}, {
  code: "JP",
  name: "Japan",
  dial: "+81",
  flag: "🇯🇵"
}, {
  code: "CN",
  name: "China",
  dial: "+86",
  flag: "🇨🇳"
}, {
  code: "IN",
  name: "Indien",
  dial: "+91",
  flag: "🇮🇳"
}, {
  code: "BR",
  name: "Brasilien",
  dial: "+55",
  flag: "🇧🇷"
}, {
  code: "MX",
  name: "Mexiko",
  dial: "+52",
  flag: "🇲🇽"
}, {
  code: "ZA",
  name: "Südafrika",
  dial: "+27",
  flag: "🇿🇦"
}, {
  code: "AE",
  name: "Vereinigte Arabische Emirate",
  dial: "+971",
  flag: "🇦🇪"
}, {
  code: "SA",
  name: "Saudi-Arabien",
  dial: "+966",
  flag: "🇸🇦"
}, {
  code: "IL",
  name: "Israel",
  dial: "+972",
  flag: "🇮🇱"
}, {
  code: "TR",
  name: "Türkei",
  dial: "+90",
  flag: "🇹🇷"
}, {
  code: "KR",
  name: "Südkorea",
  dial: "+82",
  flag: "🇰🇷"
}, {
  code: "SG",
  name: "Singapur",
  dial: "+65",
  flag: "🇸🇬"
}, {
  code: "HK",
  name: "Hongkong",
  dial: "+852",
  flag: "🇭🇰"
}, {
  code: "TW",
  name: "Taiwan",
  dial: "+886",
  flag: "🇹🇼"
}, {
  code: "TH",
  name: "Thailand",
  dial: "+66",
  flag: "🇹🇭"
}, {
  code: "MY",
  name: "Malaysia",
  dial: "+60",
  flag: "🇲🇾"
}, {
  code: "ID",
  name: "Indonesien",
  dial: "+62",
  flag: "🇮🇩"
}, {
  code: "PH",
  name: "Philippinen",
  dial: "+63",
  flag: "🇵🇭"
}, {
  code: "VN",
  name: "Vietnam",
  dial: "+84",
  flag: "🇻🇳"
}, {
  code: "EG",
  name: "Ägypten",
  dial: "+20",
  flag: "🇪🇬"
}, {
  code: "NG",
  name: "Nigeria",
  dial: "+234",
  flag: "🇳🇬"
}, {
  code: "KE",
  name: "Kenia",
  dial: "+254",
  flag: "🇰🇪"
}, {
  code: "AR",
  name: "Argentinien",
  dial: "+54",
  flag: "🇦🇷"
}, {
  code: "CL",
  name: "Chile",
  dial: "+56",
  flag: "🇨🇱"
}, {
  code: "CO",
  name: "Kolumbien",
  dial: "+57",
  flag: "🇨🇴"
}, {
  code: "PE",
  name: "Peru",
  dial: "+51",
  flag: "🇵🇪"
}] as const;
type Country = (typeof COUNTRIES)[number];
interface PhoneInputProps {
  /** Current value (full phone number with country code) */
  value: string;
  /** Called when value changes */
  onChange: (value: string) => void;
  /** Placeholder for the number input */
  placeholder?: string;
  /** Additional class for the container */
  className?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Autofocus the input */
  autoFocus?: boolean;
  /** ID for the input element */
  id?: string;
  /** `name` on the number input — password managers key autofill off it. */
  name?: string;
  /** Autofill purpose. Default "tel"; pass "off" to opt out. */
  autoComplete?: string;
  /** Marks the number input required (aria + native semantics). */
  required?: boolean;
  /** Renders the invalid state and sets aria-invalid. */
  error?: boolean;
  /** id of the element describing the field (hint or error message). */
  describedById?: string;
  /** @deprecated Use CSS variable --tenant-primary instead. Kept for backward compatibility. */
  primaryColor?: string;
}

/**
 * Split a full number into a known country + the national remainder. Pure so
 * both the lazy initial state and the re-sync effect below use one definition.
 */
function parseValue(value: string): {
  country: Country;
  local: string;
} {
  if (value) {
    const country = COUNTRIES.find(c => value.startsWith(c.dial));
    if (country) {
      return {
        country,
        local: value.slice(country.dial.length).trim()
      };
    }
    // Unknown country code, or a bare local number: keep it in the number field.
    return {
      country: COUNTRIES[0],
      local: value
    };
  }
  return {
    country: COUNTRIES[0],
    local: ""
  };
}

/**
 * International phone number input with country code selector.
 * Defaults to Germany (+49) but supports all major countries.
 *
 * @example
 * <PhoneInput
 *   value={phone}
 *   onChange={setPhone}
 *   placeholder="170 1234567"
 * />
 */
export function PhoneInput(t0) {
  const $ = _c(39);
  const {
    value,
    onChange,
    placeholder: t1,
    className: t2,
    disabled: t3,
    autoFocus: t4,
    id,
    name,
    autoComplete: t5,
    required: t6,
    error: t7,
    describedById
  } = t0;
  const placeholder = t1 === undefined ? "170 1234567" : t1;
  const className = t2 === undefined ? "" : t2;
  const disabled = t3 === undefined ? false : t3;
  const autoFocus = t4 === undefined ? false : t4;
  const autoComplete = t5 === undefined ? "tel" : t5;
  const required = t6 === undefined ? false : t6;
  const error = t7 === undefined ? false : t7;
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  let t8;
  if ($[0] !== value) {
    t8 = () => parseValue(value).country;
    $[0] = value;
    $[1] = t8;
  } else {
    t8 = $[1];
  }
  const [selectedCountry, setSelectedCountry] = useState(t8);
  let t9;
  if ($[2] !== value) {
    t9 = () => parseValue(value).local;
    $[2] = value;
    $[3] = t9;
  } else {
    t9 = $[3];
  }
  const [localNumber, setLocalNumber] = useState(t9);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const numberInputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  let t10;
  if ($[4] !== onChange) {
    t10 = () => {
      onChangeRef.current = onChange;
    };
    $[4] = onChange;
    $[5] = t10;
  } else {
    t10 = $[5];
  }
  useEffect(t10);
  let t11;
  if ($[6] !== localNumber || $[7] !== selectedCountry.dial) {
    t11 = !localNumber ? "" : localNumber.trimStart().startsWith("+") ? localNumber : `${selectedCountry.dial}${localNumber.replace(/^0+/, "")}`;
    $[6] = localNumber;
    $[7] = selectedCountry.dial;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  const composed = t11;
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    if (value !== composed) {
      const parsed = parseValue(value);
      setSelectedCountry(parsed.country);
      setLocalNumber(parsed.local);
    }
  }
  let t12;
  let t13;
  if ($[9] !== composed) {
    t12 = () => {
      onChangeRef.current(composed);
    };
    t13 = [composed];
    $[9] = composed;
    $[10] = t12;
    $[11] = t13;
  } else {
    t12 = $[10];
    t13 = $[11];
  }
  useEffect(t12, t13);
  let t14;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = function handleNumberInput(next) {
      if (/^\s*(\+|00)/.test(next)) {
        const e164 = formatPhoneNumber(next);
        const country = COUNTRIES.find(c => e164.startsWith(c.dial));
        if (country) {
          setSelectedCountry(country);
          setLocalNumber(e164.slice(country.dial.length));
          return;
        }
        setLocalNumber(next.replace(/[^\d\s+-]/g, ""));
        return;
      }
      setLocalNumber(next.replace(/[^\d\s-]/g, ""));
    };
    $[12] = t14;
  } else {
    t14 = $[12];
  }
  const handleNumberInput = t14;
  let t15;
  bb0: {
    if (!search) {
      t15 = COUNTRIES;
      break bb0;
    }
    let t16;
    if ($[13] !== search) {
      const searchLower = search.toLowerCase();
      t16 = COUNTRIES.filter(c_0 => c_0.name.toLowerCase().includes(searchLower) || c_0.dial.includes(search) || c_0.code.toLowerCase().includes(searchLower));
      $[13] = search;
      $[14] = t16;
    } else {
      t16 = $[14];
    }
    t15 = t16;
  }
  const filteredCountries = t15;
  let t16;
  let t17;
  if ($[15] !== isOpen) {
    t16 = () => {
      const handleClickOutside = function handleClickOutside(event) {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
          setSearch("");
        }
      };
      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
      }
    };
    t17 = [isOpen];
    $[15] = isOpen;
    $[16] = t16;
    $[17] = t17;
  } else {
    t16 = $[16];
    t17 = $[17];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[18] !== isOpen) {
    t18 = () => {
      if (isOpen && searchInputRef.current) {
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    };
    t19 = [isOpen];
    $[18] = isOpen;
    $[19] = t18;
    $[20] = t19;
  } else {
    t18 = $[19];
    t19 = $[20];
  }
  useEffect(t18, t19);
  const handleSelectCountry = country_0 => {
    setSelectedCountry(country_0);
    setIsOpen(false);
    setSearch("");
    setTimeout(() => numberInputRef.current?.focus(), 0);
  };
  let t20;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = e => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setSearch("");
      }
    };
    $[21] = t20;
  } else {
    t20 = $[21];
  }
  const handleKeyDown = t20;
  let t21;
  if ($[22] !== disabled || $[23] !== isOpen) {
    t21 = () => !disabled && setIsOpen(!isOpen);
    $[22] = disabled;
    $[23] = isOpen;
    $[24] = t21;
  } else {
    t21 = $[24];
  }
  let t22;
  if ($[25] !== selectedCountry.flag) {
    t22 = <span className="text-xl">{selectedCountry.flag}</span>;
    $[25] = selectedCountry.flag;
    $[26] = t22;
  } else {
    t22 = $[26];
  }
  let t23;
  if ($[27] !== selectedCountry.dial) {
    t23 = <span className="text-sm text-foreground-muted">{selectedCountry.dial}</span>;
    $[27] = selectedCountry.dial;
    $[28] = t23;
  } else {
    t23 = $[28];
  }
  const t24 = `w-4 h-4 text-foreground-muted transition-transform ${isOpen ? "rotate-180" : ""}`;
  let t25;
  if ($[29] !== t24) {
    t25 = <ChevronDown className={t24} />;
    $[29] = t24;
    $[30] = t25;
  } else {
    t25 = $[30];
  }
  let t26;
  if ($[31] !== disabled || $[32] !== isOpen || $[33] !== t21 || $[34] !== t22 || $[35] !== t23 || $[36] !== t25) {
    t26 = <button type="button" onClick={t21} disabled={disabled} className="flex items-center gap-1 px-3 py-3 border border-r-0 border-surface-dim rounded-l-xl bg-surface-container-low hover:bg-surface-container-low transition-colors disabled:opacity-50 disabled:cursor-not-allowed" aria-label={"Land ausw\xE4hlen"} aria-expanded={isOpen} aria-haspopup="listbox">{t22}{t23}{t25}</button>;
    $[31] = disabled;
    $[32] = isOpen;
    $[33] = t21;
    $[34] = t22;
    $[35] = t23;
    $[36] = t25;
    $[37] = t26;
  } else {
    t26 = $[37];
  }
  let t27;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = e_0 => handleNumberInput(e_0.target.value);
    $[38] = t27;
  } else {
    t27 = $[38];
  }
  return <div className={`relative ${className}`} ref={dropdownRef}><div className="flex">{t26}<input ref={numberInputRef} id={id} name={name} type="tel" autoComplete={autoComplete} required={required} aria-invalid={error || undefined} aria-describedby={describedById} value={localNumber} onChange={t27} placeholder={placeholder} disabled={disabled} autoFocus={autoFocus} className={`flex-1 px-4 py-3 border rounded-r-xl focus:outline-none focus:ring-2 focus:ring-tenant-primary focus:border-transparent disabled:opacity-50 disabled:bg-surface-container-low ${error ? "border-error" : "border-surface-dim"}`} onKeyDown={handleKeyDown} /></div>{isOpen && <div className="absolute z-50 left-0 top-full mt-1 w-72 bg-white border border-surface-dim rounded-xl shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200" onKeyDown={handleKeyDown}><div className="p-2 border-b border-surface-container-low"><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-muted" /><input ref={searchInputRef} type="text" value={search} onChange={e_1 => setSearch(e_1.target.value)} placeholder="Land suchen..." className="w-full pl-9 pr-3 py-2 text-sm border border-surface-dim rounded-lg focus:outline-none focus:ring-2 focus:ring-tenant-primary" /></div></div><div className="max-h-60 overflow-y-auto" role="listbox" aria-label={"L\xE4nder"}>{filteredCountries.length === 0 ? <div className="px-4 py-3 text-sm text-foreground-muted text-center">Kein Land gefunden</div> : filteredCountries.map(country_1 => <button key={country_1.code} type="button" role="option" aria-selected={selectedCountry.code === country_1.code} onClick={() => handleSelectCountry(country_1)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-surface-container-low transition-colors ${selectedCountry.code === country_1.code ? "bg-surface-container-low" : ""}`}><span className="text-xl" aria-hidden="true">{country_1.flag}</span><span className="flex-1 text-sm text-foreground">{country_1.name}</span><span className="text-sm text-foreground-muted">{country_1.dial}</span></button>)}</div></div>}</div>;
}

/**
 * Get the country by dial code
 */
export function getCountryByDialCode(dialCode: string): Country | undefined {
  return COUNTRIES.find(c => c.dial === dialCode);
}

/**
 * Parse a phone number string into country and local number
 */
export function parsePhoneNumber(phone: string): {
  country: Country | null;
  localNumber: string;
} {
  if (!phone) return {
    country: null,
    localNumber: ""
  };
  const country = COUNTRIES.find(c => phone.startsWith(c.dial));
  if (country) {
    return {
      country,
      localNumber: phone.slice(country.dial.length).trim()
    };
  }
  return {
    country: null,
    localNumber: phone
  };
}
export { COUNTRIES };
export type { Country };